import { cn, Dialog, DialogContent, DialogTitle } from "@k3-tech/react-kit";
import { Minus, Plus, RotateCcw, X } from "lucide-react";
import {
	type PointerEvent as ReactPointerEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const SCALE_STEP = 0.25;

const clampScale = (value: number) =>
	Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

export interface ImagePreviewDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	src?: string | null;
	alt?: string;
	title?: string;
}

export default function ImagePreviewDialog({
	open,
	onOpenChange,
	src,
	alt = "Image preview",
	title = "Image preview",
}: ImagePreviewDialogProps) {
	const [scale, setScale] = useState(1);
	const [offset, setOffset] = useState({ x: 0, y: 0 });
	const containerRef = useRef<HTMLDivElement>(null);
	const dragState = useRef<{
		pointerId: number;
		startX: number;
		startY: number;
		originX: number;
		originY: number;
	} | null>(null);

	const reset = useCallback(() => {
		setScale(1);
		setOffset({ x: 0, y: 0 });
	}, []);

	useEffect(() => {
		reset();
	}, [reset, src, open]);

	const zoomBy = useCallback((delta: number) => {
		setScale((prev) => {
			const next = clampScale(prev + delta);
			if (next <= MIN_SCALE) setOffset({ x: 0, y: 0 });
			return next;
		});
	}, []);

	useEffect(() => {
		const node = containerRef.current;
		if (!node || !open) return;

		const handleWheel = (event: WheelEvent) => {
			event.preventDefault();
			zoomBy(event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP);
		};

		node.addEventListener("wheel", handleWheel, { passive: false });
		return () => node.removeEventListener("wheel", handleWheel);
	}, [open, zoomBy]);

	const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (scale <= MIN_SCALE) return;
		event.currentTarget.setPointerCapture(event.pointerId);
		dragState.current = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			originX: offset.x,
			originY: offset.y,
		};
	};

	const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
		const drag = dragState.current;
		if (!drag || drag.pointerId !== event.pointerId) return;
		setOffset({
			x: drag.originX + (event.clientX - drag.startX),
			y: drag.originY + (event.clientY - drag.startY),
		});
	};

	const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (dragState.current?.pointerId === event.pointerId) {
			dragState.current = null;
		}
	};

	const zoomed = scale > MIN_SCALE;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="max-w-[50vw] w-[50vw] h-[80vh] border-0 bg-black/90 p-0 shadow-2xl sm:max-w-[50vw]"
			>
				<DialogTitle className="sr-only">{title}</DialogTitle>

				<button
					type="button"
					className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
					onClick={() => onOpenChange(false)}
					aria-label="Close preview"
				>
					<X className="h-5 w-5" />
				</button>

				<div
					ref={containerRef}
					className={cn(
						"relative flex h-full w-full items-center justify-center overflow-hidden rounded-lg",
						zoomed ? (dragState.current ? "cursor-grabbing" : "cursor-grab") : "cursor-zoom-in",
					)}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerLeave={onPointerUp}
					onDoubleClick={() => (zoomed ? reset() : zoomBy(SCALE_STEP * 4))}
				>
					{src ? (
						<img
							src={src}
							alt={alt}
							draggable={false}
							className="max-h-full max-w-full select-none object-contain transition-transform duration-100"
							style={{
								transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
							}}
						/>
					) : null}
				</div>

				<div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-white backdrop-blur">
					<button
						type="button"
						className="inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-white/15 disabled:opacity-40"
						onClick={() => zoomBy(-SCALE_STEP)}
						disabled={scale <= MIN_SCALE}
						aria-label="Zoom out"
					>
						<Minus className="h-5 w-5" />
					</button>
					<span className="min-w-12 text-center text-sm tabular-nums">
						{Math.round(scale * 100)}%
					</span>
					<button
						type="button"
						className="inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-white/15 disabled:opacity-40"
						onClick={() => zoomBy(SCALE_STEP)}
						disabled={scale >= MAX_SCALE}
						aria-label="Zoom in"
					>
						<Plus className="h-5 w-5" />
					</button>
					<button
						type="button"
						className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-white/15 disabled:opacity-40"
						onClick={reset}
						disabled={!zoomed}
						aria-label="Reset zoom"
					>
						<RotateCcw className="h-4 w-4" />
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
